/**
 * Website-specific composer.
 *
 * Wraps `useAppComposer` with a `formatSubmit` override that merges the
 * currently-selected page route into every outgoing payload. Langgraph's
 * `ActivePageFocus` middleware reads `activePageRoute` off the graph state
 * and scopes default edits to that page — so every user turn needs to
 * include it, not just the first.
 *
 * We read directly from the Zustand store at submit time (not through a
 * hook) so the latest picker selection is always captured, even if the
 * composer instance was created when a different page was active.
 */
import { useMemo, useRef } from "react";
import { createComposer, type ComposerContent } from "@launch10/use-langgraph-stream";
import { defaultFormatSubmit } from "@launch10/use-langgraph-stream";
import { UploadsAPIService } from "@rails_api_base";
import { validateFile } from "~/types/attachment";
import { uploadFile } from "~/lib/uploadPipeline";
import { useJwt, useRootPath } from "~/stores/sessionStore";
import { useWebsiteId } from "~/stores/projectStore";
import { usePreviewStore } from "~/stores/previewStore";

function formatSubmitWithActivePage(content: ComposerContent): Record<string, unknown> {
  const base = defaultFormatSubmit(content);
  const activePageRoute = usePreviewStore.getState().route;
  return activePageRoute ? { ...base, activePageRoute } : base;
}

export function useWebsiteComposer() {
  const jwt = useJwt();
  const rootPath = useRootPath();

  // Read websiteId via a ref so the composer can be memoized while still
  // picking up the latest value on each upload. On the website page the
  // id is always known; the ref is here for symmetry with the brainstorm
  // composer where the id flips mid-conversation.
  const websiteId = useWebsiteId();
  const websiteIdRef = useRef(websiteId);
  websiteIdRef.current = websiteId;

  return useMemo(() => {
    const uploadService = new UploadsAPIService({
      jwt: jwt ?? "",
      baseUrl: rootPath ?? "",
    });

    return createComposer({
      formatSubmit: formatSubmitWithActivePage,
      attachments: {
        upload: async (file: File) => {
          const response = await uploadFile(uploadService, file, {
            isLogo: false,
            websiteId: websiteIdRef.current ?? undefined,
          });
          // `upload_id` rides additional_kwargs.metadata.attachments so the
          // agent can place an image as a real website asset without a
          // search (recommendation panels' image fields depend on this).
          return {
            url: response.url,
            meta: { filename: response.filename, upload_id: response.id },
          };
        },
        validate: validateFile,
      },
    });
  }, [jwt, rootPath]);
}
